import React from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import loadable from '@loadable/component';

import { propTypes } from '../../util/types';

import { sendVerificationEmail, hasCurrentUserErrors } from '../../ducks/user.duck';
import { logout, authenticationInProgress } from '../../ducks/auth.duck';
import { manageDisableScrolling } from '../../ducks/ui.duck';

const Topbar = loadable(() => import(/* webpackChunkName: "Topbar" */ './Topbar/Topbar'));

/**
 * The trainer own profile listing, with the freshest metadata the app holds.
 *
 * `currentUserProfileListing` is only refreshed when the current user is
 * refetched — at app boot, on login, and on the Manage Listings page. Any page
 * that loads the listing through an own-listing endpoint (the listing page, the
 * edit wizard, Manage Listings) puts a newer copy in the marketplace entity
 * store, so prefer that when it is there.
 *
 * Without this the sitewide strip would keep showing boot-time status while the
 * full banner on the listing page already shows the current one — the two
 * surfaces disagreeing, which is exactly what they exist to prevent.
 *
 * Only `ownListing` entities count. The public `listing` entities that the
 * search and landing pages load carry a trimmed copy of metadata — a trainer own
 * listing came back from search with just `{ priority: 400 }` — so treating
 * those as "fresher" wiped the subscription fields and made the strip announce
 * "get a plan" to a subscribed trainer.
 */
export const selectProfileListingWithFreshMetadata = state => {
  const profileListing = state.user.currentUserProfileListing;
  if (!profileListing) {
    return null;
  }
  const entities = state.marketplaceData?.entities || {};
  const fresh = entities.ownListing?.[profileListing.id];
  const freshMetadata = fresh?.attributes?.metadata;

  return freshMetadata ? { ...profileListing, metadata: freshMetadata } : profileListing;
};

/**
 * Topbar container component, which is connected to Redux Store.
 * @component
 * @param {Object} props
 * @returns {JSX.Element}
 */
export const TopbarContainerComponent = props => {
  const { notificationCount = 0, ...rest } = props;

  return <Topbar notificationCount={notificationCount} {...rest} />;
};

const mapStateToProps = state => {
  // Topbar needs isAuthenticated and isLoggedInAs
  const { isAuthenticated, isLoggedInAs, logoutError, authScopes } = state.auth;
  // Topbar needs user info.
  const {
    currentUser,
    currentUserHasListings,
    currentUserPendingListing,
    currentUserHasOrders,
    currentUserNotificationCount: notificationCount,
    sendVerificationEmailInProgress,
    sendVerificationEmailError,
  } = state.user;
  const hasGenericError = !!(logoutError || hasCurrentUserErrors(state));
  return {
    authInProgress: authenticationInProgress(state),
    currentUser,
    currentUserHasListings,
    currentUserPendingListing,
    currentUserProfileListing: selectProfileListingWithFreshMetadata(state),
    currentUserHasOrders,
    notificationCount,
    isAuthenticated,
    isLoggedInAs,
    authScopes,
    sendVerificationEmailInProgress,
    sendVerificationEmailError,
    hasGenericError,
  };
};

const mapDispatchToProps = dispatch => ({
  onLogout: historyPush => dispatch(logout(historyPush)),
  onManageDisableScrolling: (componentId, disableScrolling) =>
    dispatch(manageDisableScrolling(componentId, disableScrolling)),
  onResendVerificationEmail: () => dispatch(sendVerificationEmail()),
});

// Note: it is important that the withRouter HOC is **outside** the
// connect HOC, otherwise React Router won't rerender any Route
// components since connect implements a shouldComponentUpdate
// lifecycle hook.
//
// See: https://github.com/ReactTraining/react-router/issues/4671
const TopbarContainer = compose(
  withRouter,
  connect(
    mapStateToProps,
    mapDispatchToProps
  )
)(TopbarContainerComponent);

export default TopbarContainer;
